import { computed, defineComponent, h } from 'vue'
import { QRating } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'

/**
 * The star rating. Read-only unless a `readonly` attribute says otherwise — most of the app displays
 * ratings rather than collecting them.
 *
 * Renders `roundedValue` rather than the raw value: QRating only draws whole and half stars, so a 4.3
 * average has to land on one of them. https://app.shortcut.com/booksprout/story/7409/partial-star-ratings
 */
export default defineComponent({
  name: 'BsRating',

  // See BsInput: undeclared DOM-named events are bound twice, once natively.
  emits: [ 'input', 'update:modelValue' ],

  props: { ...bsComponentProps },

  setup (props, { emit }) {
    const vm = useVm()
    const bs = useBsComponent({
      vm,
      props,
      emit,
      autoSaveMethod: '',
      // A rating of 0 is no rating, so `required` means a star was picked.
      isComponentValid: (val?: any) => val === true
    })

    bs.forceTriggerOnChange.value = true

    const roundedValue = computed(() => {
      if (!bs.componentValue.value) {
        return 0
      }

      const wholeNumber = Math.floor(bs.componentValue.value)
      const decimalPart = bs.componentValue.value % 1

      if (decimalPart < .25) {
        return wholeNumber
      } else if (decimalPart >= .25 && decimalPart < .75) {
        return wholeNumber + .5
      } else {
        return wholeNumber + 1
      }
    })

    return {
      ...bs,
      roundedValue
    }
  },

  render (): any {
    return h(QRating, {
      class: 'bs-rating',
      ...this.getRenderProps({
        color: 'grey',
        icon: 'app:star-empty',
        iconSelected: 'app:star-full',
        iconHalf: 'app:star-half',
        noDimming: true,
        readonly: this.$attrs.readonly === void 0 ? true : this.$attrs.readonly,
        // Quasar 2's v-model prop and event; the bundle's own API stays `value` / `input`.
        modelValue: this.roundedValue
      }),
      ...quasarHandlers(this.getListeners()),
      ...this.getAttributes()
    }, this.getSlots())
  }
})
